import { Employee, IntegrationTypeEnum, Role } from "@finway-group/shared/lib/models"
import { useSelector } from "react-redux"
import { useIntegrationConfigurations } from "~Shared/queries/integration.queries"
import { store } from "~Shared/store"
import { RootState } from "~Shared/store/rootState.interface"
import { filterEmployees } from "~Shared/utils/employee.utils"
import { employeeSorter } from "~Shared/utils/helper.utils"
import { useAuth } from "./auth.hooks"
import { useCompany } from "./company.hooks"

interface EmployeeQuery {
    excludeDeleted?: boolean
    includeAutoApprover?: boolean
    searchValue?: string
    companyId?: string
}

const DEFAULT_EMPLOYEE_QUERY = {
    excludeDeleted: false,
    includeAutoApprover: false,
    searchValue: undefined,
    companyId: undefined,
}

/**
 * @deprecated Use src/shared/queries/employee.queries.ts instead
 */
export const useEmployees = ({ excludeDeleted, includeAutoApprover, searchValue, companyId }: EmployeeQuery = DEFAULT_EMPLOYEE_QUERY) => {
    const employeesResult = useSelector(({ employees }: RootState) => employees.items)
    const currentCompanyId = useCompany()._id
    return filterEmployees(employeesResult, companyId ?? currentCompanyId, excludeDeleted, includeAutoApprover, searchValue).sort(employeeSorter)
}

export const useCardRootUser = () => useEmployees({ excludeDeleted: true }).find((employee) => employee.activeCompanyProfile?.weavrData?.isRoot)

export const getEmployeesFromStore = ({ excludeDeleted, includeAutoApprover, searchValue, companyId }: EmployeeQuery = DEFAULT_EMPLOYEE_QUERY) => {
    const employees = store.getState().employees.items
    const selectedCompanyId = store.getState().company.item._id
    return filterEmployees(employees, companyId ?? selectedCompanyId, excludeDeleted, includeAutoApprover, searchValue)
}

export const useEmployeeById = (id: string | undefined) => useEmployees().find((employee: Employee) => employee.id === id)

/**
 * @deprecated Use src/shared/queries/employee.queries.ts instead
 */
export const useLoggedInEmployee = () => {
    const { userId } = useAuth()
    return new Employee(useSelector(({ employees: { items } }: RootState) => items.find((employee) => employee.id === userId)))
}

export const useEmployeeLanguage = () => useLoggedInEmployee().getUserLanguage()

export const useEmployeesWithEditingRights = ({ excludeDeleted, rolesMap }: { excludeDeleted: boolean; rolesMap: Map<string, Role> }) => {
    const employeesExcludeDeleted = useEmployees({ excludeDeleted })
    return employeesExcludeDeleted
        .filter((e) => !rolesMap.get(e.activeCompanyProfile?.roleId)?.hasNoWriteRights)
        .sort((employeeA: Employee, employeeB: Employee) => `${employeeA.firstName} ${employeeA.lastName}`.localeCompare(`${employeeB.firstName} ${employeeB.lastName}`))
}

export const useIsUserAbsenceBeingSynced = (employee: Employee | null | undefined) => {
    const integrations = useIntegrationConfigurations()
    if (!employee) {
        return { loading: false, isAbsenceBeingSynced: false }
    }
    const personioIntegration = integrations?.data?.[IntegrationTypeEnum.PERSONIO]
    const isPersonioUser = Boolean(employee.source?.id && employee.source?.system === IntegrationTypeEnum.PERSONIO)
    const isPersonioIntegrationEnabled = personioIntegration?.isEnabled ?? false
    const isPersonioAbsenceSyncEnabled = personioIntegration?.attributes?.enableAbsenceSync ?? false
    const isAbsenceBeingSynced = isPersonioUser && isPersonioIntegrationEnabled && isPersonioAbsenceSyncEnabled
    return { loading: integrations.isFetching, isAbsenceBeingSynced }
}
